import React, {
  useState,
  useEffect,
  useReducer,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  Link,
  Stack,
  TextField,
  CircularProgress,
  Skeleton,
  Button,
  Switch,
  Card,
  List,
  ListItem,
} from "@mui/material";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";
import SendIcon from "@mui/icons-material/Send";
import { useTemplateController } from "context";
import { I18n } from "aws-amplify/utils";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { styled } from "@mui/system";
import { hrPostUserPrompt } from "layouts/dashboard/apiService";
import Close from "@mui/icons-material/Close";
import {
  BugReport,
  ContactSupport,
  HorizontalRule,
  NoMeals,
  HelpCenter,
  InfoOutlined,
} from "@mui/icons-material";
import MoreVert from "@mui/icons-material/MoreVert";
import "./dotsLoader.css";
import dayjs from "dayjs";
import { get } from "aws-amplify/api";

function ChatBot({
  open,
  onClose,
  onBotResponse,
  onMinimize,
  messages,
  setMessages,
  handleLayerSpan,
  layerSpan,
  setLayerSpan,
}) {
  /* -------------------------------------------------------- USESTATE -------------------------------------------*/
  const [controller] = useTemplateController();
  const { direction, layout, sidenavColor, darkSidenav, darkMode } = controller;
  const theme = useTheme();
  const laptopDevice = useMediaQuery("(min-width:601px)");
  const [botLoading, setBotLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");

  const [botResponse, setBotResponse] = useState(null);
  const endOfMessageRef = useRef(null);
  const [isBetaMode, setIsBetaMode] = useState(false);
  const [isChatMenuOpen, setIsChatMenuOpen] = useState(false);
  const [isChatHelpOpen, setIsChatHelpOpen] = useState(false);
  const [showDisclaimerBox, setShowDisclaimerBox] = useState(true);
  const helpCenterRef = useRef(null);
  const [savedMessages, setSavedMessages] = useState([]);
  /* -----------------------------------------------------STYLES -------------------------------------------*/

  const MessageArea = styled(Box)({
    flex: 1,
    overflowY: "auto",
    padding: "20px",
    backgroundColor: darkMode ? "#1F2020" : "#f9fafb",
    scrollbarWidth: "2px",
    scrollbarColor: darkMode ? "#555" : "#ccc #f9fafb",
    paddingTop: "1rem",
  });

  const MessageBubble = styled(Box)(({ sent, theme }) => ({
    marginBottom: "2%",
    padding: "3%",
    maxWidth: "70%",
    wordWrap: "break-word",
    borderRadius: "1rem",
    boxShadow: sent
      ? "0px 6px 18px rgba(72, 126, 212, 0.5)"
      : " 0px 6px 18px rgba(0, 0, 0, 0.25)",
    backgroundColor: sent
      ? darkMode
        ? "#1E2F6E"
        : "#067BC2" //#3f77c3
      : darkMode
      ? "#282828"
      : "#e0e0e0",
    color: sent
      ? theme.palette.primary.contrastText
      : theme.palette.text.primary,
    alignSelf: sent ? "flex-end" : "flex-start",
    whiteSpace: "pre-line",
  }));

  const welcomeMessage =
    "Hello! I'm your Smart Report Assistant. I'm here to support you in navigating your organization's hierarchy.";

  const botConfirmMessages = [
    (userMessage) =>
      `Based on your request for "${userMessage}", here's what i found. Would you like help with anything else?`,
    (userMessage) =>
      `You asked about "${userMessage}", here are the results i gathered.`,
    (userMessage) => `Here's what i found for your query; "${userMessage}".`,
    (userMessage) =>
      `Regarding your search for "${userMessage}", these might help.`,
    (userMessage) =>
      `You mentioned "${userMessage}". Let's take a look at the related data.`,
    (userMessage) =>
      `Here are some results related to "${userMessage}" as requested.`,
    (userMessage) =>
      `Results for your inquiry about "${userMessage}" are listed below. Let me know if you need anything else.`,
  ];

  const randomConfirmMessage =
    botConfirmMessages[Math.floor(Math.random() * botConfirmMessages.length)];
  const botMessage = randomConfirmMessage(newMessage);

  const helpKeywords = ["help", "what", "explain", "tell", "info", "about"];
  const improveSynonyms = ["improve", "better", "enhance", "optimize", "fix"];
  const improveActionKeywords = ["can", "how", "what", "could", "should", "will"];
  const improveContextKeywords = ["span", "layer", "help"];

  const isHelpRequest = (text) => {
    const normalized = text.trim().toLowerCase();
    const hasHelpKeyword = helpKeywords.some(keyword => normalized.includes(keyword));
    return hasHelpKeyword || normalized.includes("what can you do");
  };

  const isHelpSpanRequest = (text) => {
    const normalized = text.trim().toLowerCase();
    const hasHelpKeyword = helpKeywords.some(keyword => normalized.includes(keyword));
    return hasHelpKeyword && normalized.includes("span");
  };

  const isHelpLayerRequest = (text) => {
    const normalized = text.trim().toLowerCase();
    const hasHelpKeyword = helpKeywords.some(keyword => normalized.includes(keyword));
    return hasHelpKeyword && normalized.includes("layer");
  };

  const isHelpLayerSpanRequest = (text) => {
    const normalized = text.trim().toLowerCase();
    const hasHelpKeyword = helpKeywords.some(keyword => normalized.includes(keyword));
    return hasHelpKeyword && normalized.includes("layer") && normalized.includes("span");
  };

  const isImproveRequest = (text) => {
    const normalized = text.trim().toLowerCase();
    if (normalized.includes("why")) return false;
    const hasImproveSynonym = improveSynonyms.some(syn => normalized.includes(syn));
    const hasActionKeyword = improveActionKeywords.some(keyword => normalized.includes(keyword));
    const hasContextKeyword = improveContextKeywords.some(keyword => normalized.includes(keyword));
    return hasImproveSynonym && (hasActionKeyword || hasContextKeyword);
  };

  const isWhyImproveRequest = (text) => {
    const normalized = text.trim().toLowerCase();
    const hasImproveSynonym = improveSynonyms.some(syn => normalized.includes(syn));
    return normalized.includes("why") && hasImproveSynonym;
  };

  const helpResponses = [
    "My goal is to help you better understand and navigate your company's internal structure.",
    "Just start typing what you're looking for - i'll handle the rest and guide you to the right spot.",
    "Tell me what you're interested in and i'll help you visualize it easily.",
    "I can help you browse and search through your company's structure. Try asking about locations, roles, or managers!",
  ];

  const helpSpanResponses = [
    "Span refers to the number of direct reports a manager has. According to Epta guidelines, the span should range between the number of subordinate layers and up to 3x that number. Click on each card to view detailed span analysis with color-coded status: Green (meets guidelines), Yellow (slightly outside range), or Red (action required)."
  ];

  const helpLayerResponses = [
    "Layers refer to hierarchical levels from CEO (level 0) to the lowest level. Epta's target is a maximum of six layers (CEO-6), except for Operations, Service, and Installation, which allow one additional layer. If the analysis shows non-Green status, review your structure for misalignments. Click on each card to view layer details or use the 'Hierarchy' filter."
  ];

  const helpLayerSpanResponses = [
    "Layer and Span analyses help assess organizational structure. Layers indicate hierarchical levels, while Span shows direct reports per manager. Epta guidelines suggest a maximum of six layers (CEO-6) and a span ranging from the number of subordinate layers to 3x that number. Click on each card for detailed analysis with color-coded status: Green (meets guidelines), Yellow (slightly outside range), or Red (action required). Review your structure for misalignments if non-Green statuses appear."
  ];

  const improveResponses = [
    "To improve your organizational structure, start using a useful checklist to thoroughly analyze and delve into your organizational structure. Then you can focus on several areas, such as the number of “one-to-one” managers (manager with only one direct report), how many managers are in a 1-1-1 reporting line, the number of micro teams with only 2-3 reports (and how responsibilities cascade), identifying where large staff structures exist, determining which managers have a span outside the optimal range, which managers have a span outside the average, or where there are excessive layers in the hierarchy."
  ];

  const whyImproveResponses = [
    "Improving your organizational structure provides several key benefits:\n •Improve Efficiency and Agility\n •Cost Optimization\n •Enhanced Accountability and Transparency\n •Better Talent Utilization",
  ];

  const outOfScopeResponses = [
    "Hmm, i'm not sure about that one. I can help you explore your company's structure and employee relationships.",
    "Looks like i can't help with that - but i can support you in searching and filtering your company's organizational data.",
    "My job is to help you navigate your org chart. While i can't answer that, i'm happy to assist with more.",
    "I'm here to help you explore your company's structure. That question may be out of scope, but i can assist you with anything related to your org chart.",
  ];

  const layerResponses = [
    "Sure! I can show you the layer analysis. In case this information seems inaccurate, keep in mind that you might not be able to see all the nodes due to your permissions. If the problem persists, please contact your administrator.",
    "Of course! I can display the layer analysis. If you notice any discrepancies, it may be due to your permissions. Please reach out to your administrator if the issue continues.",
    "Absolutely! I can present the layer analysis. If you find any inaccuracies, it could be related to your permissions. Don't hesitate to contact your administrator if the problem persists.",
    "Certainly! I can reveal the layer analysis. If you encounter any inconsistencies, it might be due to your permissions. Please get in touch with your administrator if the issue continues.",
  ];

  const disableLayerResponses = [
    "Sure! The layer analysis view is disabled.",
    "Of course! The layer analysis view is disabled.",
    "Absolutely! The layer analysis view is disabled.",
    "Certainly! The layer analysis view is disabled.",
  ];

  const spanResponses = [
    "Sure! I can show you the span analysis. In case this information seems inaccurate, keep in mind that you might not be able to see all the nodes due to your permissions. If the problem persists, please contact your administrator.",
    "Of course! I can display the span analysis. If you notice any discrepancies, it may be due to your permissions. Please reach out to your administrator if the issue continues.",
    "Absolutely! I can present the span analysis. If you find any inaccuracies, it could be related to your permissions. Don't hesitate to contact your administrator if the problem persists.",
    "Certainly! I can reveal the span analysis. If you encounter any inconsistencies, it might be due to your permissions. Please get in touch with your administrator if the issue continues.",
  ];

  const disableSpanResponses = [
    "Sure! The span analysis view is disabled.",
    "Of course! The span analysis view is disabled.",
    "Absolutely! The span analysis view is disabled.",
    "Certainly! The span analysis view is disabled.",
  ];

  const layerAndSpanResponses = [
    "Sure! I can show you the layer and span analysis. In case this information seems inaccurate, keep in mind that you might not be able to see all the nodes due to your permissions. If the problem persists, please contact your administrator.",
    "Of course! I can display the layer and span analysis. If you notice any discrepancies, it may be due to your permissions. Please reach out to your administrator if the issue continues.",
    "Absolutely! I can present the layer and span analysis. If you find any inaccuracies, it could be related to your permissions. Don't hesitate to contact your administrator if the problem persists.",
    "Certainly! I can reveal the layer and span analysis. If you encounter any inconsistencies, it might be due to your permissions. Please get in touch with your administrator if the issue continues.",
  ];

  const disableLayerAndSpanResponses = [
    "Sure! The layer and span analysis view is disabled.",
    "Of course! The layer and span analysis view is disabled.",
    "Absolutely! The layer and span analysis view is disabled.",
    "Certainly! The layer and span analysis view is disabled.",
  ];

  const getRandomBotResponse = (responses) =>
    responses[Math.floor(Math.random() * responses.length)];

  /* ----------------------------------------------------- HANDLERS -------------------------------------------*/
  const messageChange = (event) => {
    event.preventDefault();
    setNewMessage(event.target.value);
  };

  useEffect(() => {
    handleLayerSpan(layerSpan);
  }, [layerSpan]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        helpCenterRef.current &&
        !helpCenterRef.current.contains(event.target) &&
        isChatHelpOpen
      ) {
        setIsChatHelpOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isChatHelpOpen]);

  const handleSendMessage = async () => {
    if (newMessage.trim()) {
      const message = {
        id: messages.length + 1,
        text: newMessage,
        sender: "You",
        sent: true,
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),
      };
      setMessages((prev) => [...prev, message]); //salvo messaggio utente
      setNewMessage("");

      if (isImproveRequest(newMessage)) {
        const botMessage = {
          id: messages.length + 2,
          text: getRandomBotResponse(improveResponses),
          sender: "Assistant",
          sent: false,
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }),
        };
        setBotLoading(true);
        setTimeout(async () => {
          setMessages((prev) => [...prev, botMessage]);
          setBotLoading(false);
        }, 500);

        return;
      }

      if (isWhyImproveRequest(newMessage)) {
        const baseText = getRandomBotResponse(whyImproveResponses);
        const botText = (
          <>
            {baseText}{"\n\n"}For more information, click{" "}
            <Link
              href={"https://eptait.sharepoint.com/:f:/s/hrcompbenanalytics/IgC-3Nfks5JOSpvBNKnE1qCSAdBAPkUT_XRBeJ9x3yclln8"}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                color: "#00449E"
              }}
              >
              here
            </Link>.
          </>
        );

        const botMessage = {
          id: messages.length + 2,
          text: botText,
          sender: "Assistant",
          sent: false,
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }),
        };
        setBotLoading(true);
        setTimeout(async () => {
          setMessages((prev) => [...prev, botMessage]);
          setBotLoading(false);
        }, 500);

        return;
      }

      if (isHelpLayerSpanRequest(newMessage)) {
        const botMessage = {
          id: messages.length + 2,
          text: getRandomBotResponse(helpLayerSpanResponses),
          sender: "Assistant",
          sent: false,
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }),
        };
        setBotLoading(true);
        setTimeout(async () => {
          setMessages((prev) => [...prev, botMessage]);
          setBotLoading(false);
        }, 500);

        return;
      }

      if (isHelpSpanRequest(newMessage)) {
        const botMessage = {
          id: messages.length + 2,
          text: getRandomBotResponse(helpSpanResponses),
          sender: "Assistant",
          sent: false,
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }),
        };
        setBotLoading(true);
        setTimeout(async () => {
          setMessages((prev) => [...prev, botMessage]);
          setBotLoading(false);
        }, 500);

        return;
      }

      if (isHelpLayerRequest(newMessage)) {
        const botMessage = {
          id: messages.length + 2,
          text: getRandomBotResponse(helpLayerResponses),
          sender: "Assistant",
          sent: false,
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }),
        };
        setBotLoading(true);
        setTimeout(async () => {
          setMessages((prev) => [...prev, botMessage]);
          setBotLoading(false);
        }, 500);

        return;
      }

      if (isHelpRequest(newMessage)) {
        const botMessage = {
          id: messages.length + 2,
          text: getRandomBotResponse(helpResponses),
          sender: "Assistant",
          sent: false,
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }),
        };
        setBotLoading(true);
        setTimeout(async () => {
          setMessages((prev) => [...prev, botMessage]);
          setBotLoading(false);
        }, 500);

        return;
      }

      if (
        newMessage.toLowerCase().includes("layer") &&
        newMessage.toLowerCase().includes("span")
      ) {
        if (newMessage.toLowerCase().includes("disable")) {
          const botMessage = {
            id: messages.length + 2,
            text: getRandomBotResponse(disableLayerAndSpanResponses),
            sender: "Assistant",
            sent: false,
            time: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }),
          };
          setBotLoading(true);
          setLayerSpan(() => ({
            layer: false,
            span: false,
          }));
          setTimeout(async () => {
            setMessages((prev) => [...prev, botMessage]);
            setBotLoading(false);
          }, 500);
        } else {
          const botMessage = {
            id: messages.length + 2,
            text: getRandomBotResponse(layerAndSpanResponses),
            sender: "Assistant",
            sent: false,
            time: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }),
          };
          setBotLoading(true);
          setLayerSpan(() => ({
            layer: true,
            span: true,
          }));
          setTimeout(async () => {
            setMessages((prev) => [...prev, botMessage]);
            setBotLoading(false);
          }, 500);
        }
        return;
      }

      if (newMessage.toLowerCase().includes("layer")) {
        if (newMessage.toLowerCase().includes("disable")) {
          const botMessage = {
            id: messages.length + 2,
            text: getRandomBotResponse(disableLayerResponses),
            sender: "Assistant",
            sent: false,
            time: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }),
          };
          setBotLoading(true);
          setLayerSpan((prev) => ({
            layer: false,
            span: prev.span,
          }));
          setTimeout(async () => {
            setMessages((prev) => [...prev, botMessage]);
            setBotLoading(false);
          }, 500);
        } else {
          const botMessage = {
            id: messages.length + 2,
            text: getRandomBotResponse(layerResponses),
            sender: "Assistant",
            sent: false,
            time: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }),
          };
          setBotLoading(true);
          setLayerSpan((prev) => ({
            layer: true,
            span: prev.span,
          }));
          setTimeout(async () => {
            setMessages((prev) => [...prev, botMessage]);
            setBotLoading(false);
          }, 50);
        }

        return;
      }

      // if (isSpanRequest(newMessage)) {
      if (newMessage.toLowerCase().includes("span")) {
        if (newMessage.toLowerCase().includes("disable")) {
          const botMessage = {
            id: messages.length + 2,
            text: getRandomBotResponse(disableSpanResponses),
            sender: "Assistant",
            sent: false,
            time: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }),
          };
          setBotLoading(true);
          setLayerSpan((prev) => ({
            layer: prev.layer,
            span: false,
          }));
          setTimeout(async () => {
            setMessages((prev) => [...prev, botMessage]);
            setBotLoading(false);
          }, 500);
        } else {
          const botMessage = {
            id: messages.length + 2,
            text: getRandomBotResponse(spanResponses),
            sender: "Assistant",
            sent: false,
            time: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }),
          };
          setBotLoading(true);
          setLayerSpan((prev) => ({
            layer: prev.layer,
            span: true,
          }));
          setTimeout(() => {
            setMessages((prev) => [...prev, botMessage]);
            setBotLoading(false);
          }, 50);
        }

        return;
      }

      setTimeout(async () => {
        try {
          setBotLoading(true);
          let payload;
          if (isBetaMode) {
            const userMessages = [...messages, message]
              .filter((msg) => msg.sender === "You")
              .map((msg) => msg.text)
              .join("; ");
            payload = userMessages;
          } else {
            payload = message.text;
          }
          const data = await handleHrPostUserPrompt(payload);

          setBotResponse(data);
          onBotResponse(data);
          setBotLoading(false);
          if (data.IS_PERTINENT) {
            const assistantResponse = {
              id: Date.now(),
              text: botMessage,
              sender: "Assistant",
              sent: false,
              time: new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }),
            };
            setMessages((prev) => [...prev, assistantResponse]);
          } else {
            const assistantResponse = {
              id: Date.now(),
              text: getRandomBotResponse(outOfScopeResponses),
              sender: "Assistant",
              sent: false,
              time: new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }),
            };

            setMessages((prev) => [...prev, assistantResponse]);
          }
        } catch (error) {
          console.log(error);
        }
      }, 500);
    }
  };

  const handleHrPostUserPrompt = async (newMessage) => {
    try {
      const payload = {
        // user_prompt:lastUpdate,
        user_prompt: newMessage,
      };
      const response = await hrPostUserPrompt(payload);
      return response;
    } catch (error) {
      console.log("errore postUserPrompt in bot", error);
    }
  };

  const handleResetChat = () => {
    setMessages([
      {
        id: 1,
        text: welcomeMessage,
        sender: "Assistant",
        sent: false,
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),
      },
    ]);

    setLayerSpan({
      layer: false,
      span: false,
    });
  };

  const handleOpenChatMenu = () => {
    setIsChatMenuOpen(!isChatMenuOpen);
    setIsChatHelpOpen(false);
  };

  const handleOpenHelpCenter = () => {
    setIsChatHelpOpen(!isChatHelpOpen);
    setIsChatMenuOpen(false);
  };

  // useEffect(() => {
  //   console.log(isBetaMode);
  // }, [isBetaMode]);

  const el = document.getElementById("messageArea");
  // id of the chat container ---------- ^^^
  if (el) {
    el.scrollTop = el.scrollHeight;
  }

  function useChatScroll(dep, endOfMessageRef) {

    useLayoutEffect(() => {
      if (endOfMessageRef.current) {
        endOfMessageRef.current.scrollTop = endOfMessageRef.current.scrollHeight;
      }
    }, [dep]); // Re-run effect when 'dep' changes
  }

  useChatScroll([messages, botLoading], endOfMessageRef);
  /* -------------------------------------------------------- JSX -------------------------------------------*/
  return (
    <Box
      sx={{
        position: "fixed",
        bottom: "4%",
        right: "5%",
        zIndex: 1400,
        display: "flex",
        flexDirection: "column",
        width: "20%",
        minWidth: "280px",
        maxWidth: "415px",
        overflowY: "auto",
        height: "55%",
        borderRadius: "1rem",
        boxShadow: "0px 6px 18px rgba(0, 0, 0, 0.15)",
      }}
    >
      <Paper
        elevation={10}
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: "1rem",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            p: 1.5,
            position: "sticky",
            alignItems: "center",
            width: "100%",
            backgroundColor: darkMode ? "#282828" : "#cdd9e6ff",
            //#dbe6f3"
            borderTopLeftRadius: "1rem",
            borderTopRightRadius: "1rem",
          }}
        >
          <Box
            sx={{
              width: 40,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <IconButton onClick={handleOpenChatMenu}>
              <MoreVert />
            </IconButton>
            <Tooltip title={I18n.get("Help Center")}>
              <IconButton onClick={handleOpenHelpCenter}>
                <HelpCenter sx={{color: "#00336e"}}/>
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ width: 40 }}></Box>
          {/** spazio fittizio a sinistra */}

          <Typography
            variant="h5"
            sx={{
              color: "#00336e",
              fontWeight: "bold",
              alignSelf: "center",
              flexGrow: 1,
              textAlign: "center",
            }}
          >
            {I18n.get("Smart Report")}
            <Tooltip title={I18n.get("Smart report Assistant")}>
              <ContactSupport sx={{ marginLeft: "0.3rem" }} />
            </Tooltip>
          </Typography>
          <Tooltip title={I18n.get("Minimize")}>
            <IconButton
              onClick={onMinimize}
              sx={{
                alignItems: "end",
                justifyContent: "right",
                display: "flex",
                color: "#004493",
              }}
            >
              <HorizontalRule />
            </IconButton>
          </Tooltip>
          <Tooltip title={I18n.get("Close")}>
            <IconButton
              onClick={onClose}
              sx={{
                alignItems: "center",
                justifyContent: "right",
                display: "flex",
                color: "#004493",
              }}
            >
              <Close />
            </IconButton>
          </Tooltip>
        </Box>

        <MessageArea ref={endOfMessageRef}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 3,
            }}
          >
            <Button
              onClick={handleResetChat}
              variant="contained"
              size="small"
              sx={{
                position: "absolute",
                padding: "10px",
                borderRadius: "1.5rem",
                backgroundColor: "#983961ea", //#3f77c3 #1e5198ff #983961

                color: "rgb(255, 255, 255)",
                "&:hover": {
                  backgroundColor: "#94385E", //rgb(25, 66, 124)
                },
                "&:focus:not(:hover)": {
                  backgroundColor: "#94385E", //#1e5198ff
                  boxShadow: "none",
                },
              }}
              // endIcon={<Search />}
            >
              {I18n.get("Reset chat")}
            </Button>
          </Box>
          
            {messages.map((message) => (
              <Box
                key={message.id}
                display="flex"
                flexDirection="column"
                alignItems={message.sent ? "flex-end" : "flex-start"}
              >
                <Typography variant="caption" fontSize={"0.90rem"} mb={0.8}>
                  {/* {message.sender !== "You" ||message==messages[1] ? message.sender : ""}  */}
                  {message.sender}
                </Typography>
                <MessageBubble sent={message.sent}>
                  <Typography>{message.text}</Typography>
                  <Box
                    sx={{ margin: 0, display: "flex", justifyContent: "right" }}
                  >
                    <Typography variant="caption" fontSize={"0.70rem"}>
                      {/* {message.sender !== "You" ||message==messages[1] ? message.sender : ""}  */}
                      {message.time}
                    </Typography>
                  </Box>
                </MessageBubble>
              </Box>
            ))}
            {botLoading && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography variant="caption" fontSize={"0.90rem"} mb={0.8}>
                  Assistant
                </Typography>
                <MessageBubble sent={false}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <div className="bouncing-loader">
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                    {/* <CircularProgress size={16} thickness={5} /> */}
                  </Box>
                </MessageBubble>
              </Box>
            )}
          
        </MessageArea>

        <Box
          display="flex"
          flexDirection="column"
          gap={1}
          sx={{
            padding: "5%",
            backgroundColor: "#f9fafb",
          }}
        >
          {isChatMenuOpen && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                position: "absolute",
                justifyContent: "center",
                alignItems: "center",
                width: "80%",
                top: "10%",
                left: "1.5%",
                gap: 2,
                padding: 2,
                borderRadius: "1rem",
                boxShadow: 3,
                zIndex: 10,
                backgroundColor: "#ffffff",
              }}
            >
              <Typography variant="caption">
                {I18n.get(
                  "By enabling Beta Mode, the Smart Report Assistant remembers previous requests to pregressively combine filters."
                )}
              </Typography>
              <Switch
                checked={isBetaMode}
                onChange={() => {
                  setIsBetaMode((prevMode) => !prevMode);
                  handleResetChat();
                  setTimeout(() => {
                    setIsChatMenuOpen(false);
                  }, 1000);
                }}
                checkedIcon={<BugReport sx={{ color: "#f0f0f0" }} />}
                sx={{
                  width: 50,
                  height: 25,
                  padding: 0,
                  "& .MuiSwitch-switchBase": {
                    padding: 0.5,
                    transform: "translate(-2px, -50%)",
                    "&.Mui-checked": {
                      transform: "translate(24px, -50%)",
                    },
                  },
                  "& .MuiSwitch-thumb": {
                    width: 21,
                    height: 21,
                  },
                  // "& .MuiSwitch-switchBase.Mui-checked":{
                  //   transform:"translateX(27px)"
                  // },
                  "& .Mui-checked+.MuiSwitch-track": {
                    backgroundColor: "#7fa1ce", //riempimento
                    borderColor: "#7fa1ce", //bordo
                    // opacity:0.5
                  },
                  "& .MuiSwitch-track": {
                    "&::after": {
                      content: isBetaMode ? '"Beta"' : '""',
                      fontSize: 12,
                      position: "absolute",
                      color: "#f0f0f0",
                      top: "10%",
                      left: "3px",
                    },
                  },
                }}
              ></Switch>
            </Box>
          )}

          {isChatHelpOpen && (
            <Box
              ref={helpCenterRef}
              sx={{
                display: "flex",
                flexDirection: "column",
                position: "absolute",
                justifyContent: "center",
                width: "60%",
                top: "10%",
                left: "8%",
                gap: 1,
                padding: 2,
                borderRadius: "1rem",
                boxShadow: 3,
                zIndex: 10,
                backgroundColor: "#ffffff",
              }}
            >
              <Typography variant="caption">
                {I18n.get("Available commands:")}
              </Typography>
              <Box component="ul" sx={{ margin: 0, paddingLeft: 2.5 }}>
                <Box component="li" sx={{ marginBottom: 0.5 }}>
                  <Typography variant="caption">
                    {I18n.get("'help' for general assistance")}
                  </Typography>
                </Box>
                <Box component="li" sx={{ marginBottom: 0.5 }}>
                  <Typography variant="caption">
                    {I18n.get("'help layer/span' for analysis info")}
                  </Typography>
                </Box>
                <Box component="li" sx={{ marginBottom: 0.5 }}>
                  <Typography variant="caption">
                    {I18n.get("'how to improve layer/span' for suggestions")}
                  </Typography>
                </Box>
                <Box component="li" sx={{ marginBottom: 0.5 }}>
                  <Typography variant="caption">
                    {I18n.get("'why improve layer/span' for benefits")}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {showDisclaimerBox && (
            <Box
              sx={{
                backgroundColor: "#cdd9e6ff",
                padding: "0.75rem",
                paddingRight: "1.75rem",
                display: "flex",
                alignItems: "center",
                gap: 1,
                position: "relative",
                borderRadius: "0.5rem",
              }}
            >
              <InfoOutlined sx={{ fontSize: "12px", color: "#004493", flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: "#00336e", flex: 1 }}>
                {I18n.get("Please note that only one filter may be managed at a time. If you need to select multiple values for each field or apply filters to multiple fields simultaneously, kindly use the Filter tab into the dashboard")}
              </Typography>
              <Tooltip title={I18n.get("Close")}>
                <IconButton
                  size="small"
                  onClick={() => setShowDisclaimerBox(false)}
                  sx={{ 
                    color: "#004493", 
                    minWidth: 0,
                    padding: "2px",
                    position: "absolute",
                    top: "4px",
                    right: "4px",
                  }}
                >
                  <Close sx={{ fontSize: "16px" }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              type="text"
              variant="outlined"
              placeholder={I18n.get("Type a message...")}
              value={newMessage}
              onChange={messageChange}
              size="small"
              focused
              onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
              sx={{
                "& .MuiOutlinedInput-root ": {
                  "& fieldset": {
                    borderRadius: "1.5rem",
                  },
                },
                ".MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: "#00449E",
                  },
              }}
            />
            <Tooltip title="Send">
              <IconButton sx={{ color: "#00449E" }} onClick={handleSendMessage}>
                <SendIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default ChatBot;
